// ===========================================
// Remontée d'erreurs *capturées* vers New Relic
// ===========================================
//
// Module volontairement sans effet de bord : il est importé par plusieurs
// entrées Encore (`app`, `statistics`, …) qui cohabitent sur une même page.
// Les listeners globaux vivent dans `errors.js`, chargé par la seule entrée
// `app`, pour ne pas enregistrer deux fois `unhandledrejection`.

// Messages injectés par des scanners externes (Outlook SafeLinks, crawlers Bing/MSN,
// extensions navigateur). Ne proviennent pas de notre code : on les ignore pour ne
// pas polluer New Relic ni la console utilisateur.
const IGNORED_ERROR_MESSAGES = [
    /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/,
    // Fetch échoué / annulé — messages génériques par navigateur, non actionnables
    // (perte réseau, onglet backgroundé, navigation en cours, AbortController).
    /^Load failed$/,                                    // Safari (desktop + iOS)
    /^Failed to fetch$/,                                // Chrome / Edge
    /^NetworkError when attempting to fetch resource\.?$/, // Firefox
    // AbortController (tous navigateurs). ATTENTION : si un jour on l'utilise pour
    // un vrai timeout métier qu'on veut tracer, remonter explicitement via
    // newrelic.noticeError — sinon l'erreur sera silencée ici.
    /^The operation was aborted\.?$/,
    /^AbortError:/,
    // Bruit Chrome benign décrit dans la spec ResizeObserver, non actionnable.
    /^ResizeObserver loop /,
];

export function isIgnoredError(input) {
    const message = input instanceof Error ? input.message : String(input ?? '');
    return IGNORED_ERROR_MESSAGES.some(pattern => pattern.test(message));
}

// jqXHR à status 0 : requête annulée par le navigateur quand on quitte la page,
// ou perte réseau, non actionnable dans les deux cas. Les codes HTTP remontent.
export function isAbortedRequest(error) {
    return Boolean(error)
        && typeof error === 'object'
        && typeof error.readyState === 'number'
        && error.status === 0;
}

// Erreurs provenant d'extensions navigateur : hors de notre code, non actionnables.
const EXTENSION_SOURCE_PATTERN = /(?:chrome|moz|safari-web|safari)-extension:\/\//;

export function isExtensionSource(source) {
    return typeof source === 'string' && EXTENSION_SOURCE_PATTERN.test(source);
}

export function isExtensionError(input) {
    const stack = input instanceof Error ? input.stack : null;
    return typeof stack === 'string' && EXTENSION_SOURCE_PATTERN.test(stack);
}

// Fonction utilitaire pour récupérer un contexte utile
export function getErrorContext(additional = {}) {
    return {
        url: window.location.href,
        pageTitle: document.title,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        ...additional
    };
}

/**
 * Remonte à New Relic une erreur *déjà capturée* par un `catch` / `.fail()`.
 *
 * `window.onerror` et `unhandledrejection` ne voient que ce qui remonte jusqu'au
 * navigateur : tout ce qu'un `try/catch` avale reste invisible, y compris les
 * échecs qui vident un écran sans rien casser. `console.error` seul ne quitte
 * pas le poste de l'utilisateur — d'où ce relais explicite.
 *
 * @param {string} label   Ce qui a échoué, en clair (sert de message si pas d'Error).
 * @param {*}      [error] Erreur d'origine, jqXHR, ou n'importe quelle valeur rejetée.
 * @param {Object} [context] Données utiles au debug (route, sélecteur, code HTTP…).
 */
export function reportError(label, error = null, context = {})
{
    console.error(label, error ?? '');

    if (isIgnoredError(error ?? label) || isExtensionError(error) || isAbortedRequest(error)) {
        return;
    }

    if (window.newrelic && typeof newrelic.noticeError === 'function') {
        newrelic.noticeError(
            error instanceof Error ? error : new Error(label),
            { handled: true, label: label, context: getErrorContext(context) }
        );
    }
}
